import { useEffect, useRef } from 'react';
import { TimeMs } from '../timeline/types';
import { playheadStore } from './playhead-store';

type UsePlayheadDriverOptions = {
  duration: TimeMs;
  isPlaying: boolean;
  loop?: boolean;
  onEnd?: () => void;
};

/**
 * Drives the playhead via requestAnimationFrame while `isPlaying` is true.
 * Writes to `playheadStore` — does NOT trigger React renders by itself.
 *
 * If the playhead is at/past `duration` when play starts, it rewinds to 0.
 * When the playhead reaches `duration` and `loop` is false, parks at 0 and calls `onEnd`.
 */
export const usePlayheadDriver = ({ duration, isPlaying, loop = false, onEnd }: UsePlayheadDriverOptions): void => {
  const rafRef = useRef<number | null>(null);
  const lastTickRef = useRef<number>(0);
  const onEndRef = useRef(onEnd);

  useEffect(() => {
    onEndRef.current = onEnd;
  }, [onEnd]);

  useEffect(() => {
    if (!isPlaying) {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      return;
    }

    if (playheadStore.get() >= duration) {
      playheadStore.set(0);
    }

    lastTickRef.current = performance.now();

    const tick = (now: number) => {
      const dt = now - lastTickRef.current;
      lastTickRef.current = now;
      const next = playheadStore.get() + dt;

      if (next >= duration) {
        if (loop) {
          playheadStore.set(0);
          rafRef.current = requestAnimationFrame(tick);
          return;
        }
        // Park at 0 so the first clip stays visible after the run ends.
        playheadStore.set(0);
        rafRef.current = null;
        onEndRef.current?.();
        return;
      }

      playheadStore.set(next);
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [isPlaying, duration, loop]);
};
